import React from "react";

interface CustomPaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
}

const CustomPagination: React.FC<CustomPaginationProps> = ({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
}) => {
  const jumpSize = 1;

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxDisplayed = 5;

    if (totalPages <= maxDisplayed) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const left = Math.max(2, currentPage - 1);
      const right = Math.min(totalPages - 1, currentPage + 1);

      pages.push(1);

      if (left > 2) pages.push("left-ellipsis");
      for (let i = left; i <= right; i++) pages.push(i);
      if (right < totalPages - 1) pages.push("right-ellipsis");

      pages.push(totalPages);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 px-2 py-2">
      <div className="text-muted small text-center d-none d-sm-none d-md-flex text-md-start w-100 w-md-auto mb-2 mb-md-0">
        {`Showing ${startIndex} - ${endIndex} of ${totalItems} entries`}
      </div>

      <nav
        aria-label="Page navigation"
        className="pagination-style-1 w-100 w-md-auto d-flex justify-content-end align-items-center overflow-auto px-1"
      >
        <ul className="pagination mb-0 p-0 flex-wrap">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
              aria-label="First Page"
            >
              <i className="fas fa-angle-double-left" />
            </button>
          </li>

          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Previous Page"
            >
              <i className="fas fa-angle-left" />
            </button>
          </li>

          {pageNumbers.map((page, index) => {
            if (page === "left-ellipsis") {
              return (
                <li key={`ellipsis-left-${index}`} className="page-item">
                  <button
                    className="page-link"
                    onClick={() =>
                      onPageChange(Math.max(currentPage - jumpSize, 1))
                    }
                  >
                    ...
                  </button>
                </li>
              );
            }

            if (page === "right-ellipsis") {
              return (
                <li key={`ellipsis-right-${index}`} className="page-item">
                  <button
                    className="page-link"
                    onClick={() =>
                      onPageChange(Math.min(currentPage + jumpSize, totalPages))
                    }
                  >
                    ...
                  </button>
                </li>
              );
            }

            return (
              <li
                key={`page-${page}`}
                className={`page-item ${page === currentPage ? "active" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => onPageChange(Number(page))}
                >
                  {page}
                </button>
              </li>
            );
          })}

          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              aria-label="Next Page"
            >
              <i className="fas fa-angle-right" />
            </button>
          </li>

          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
              aria-label="Last Page"
            >
              <i className="fas fa-angle-double-right" />
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
};

export default CustomPagination;
